import React from "react"
import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../../../components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Label } from "../../../components/ui/label"
import {
  FileText,
  Upload,
  Loader2,
  Plus,
  DeleteIcon
} from "lucide-react"
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { toast } from "react-toastify";
import { Checkbox } from "../../../components/ui/checkbox";
// @ts-ignore
import { useUserContext } from "../../../Contexts/UserProvider.tsx";
import { useParams, useSearchParams } from "react-router-dom";
import { useBlockchainRequest } from "../../Services/BlockchainRequest";
import { inputFieldValidator } from "../../../Utilities/validation";
import { useFileUploader } from "../../../BlobStorage/BlobServiceClient";
import { CertificateType } from "../../../Utilities/enums"
import { Separator } from "../../../components/ui/separator"
import { DynamicFieldRenderer } from "./DynamicFieldRenderer"

interface Item {
  itemId: string;
  itemName: string;
  itemCode: string;
  itemDescription: string;
  itemSku: string;
  itemCatelogName: string;
  itemDetails: any;
  itemImageUrl: string;
}

const AddRequest = ({ isOpen, onClose, requestData, isUpdate, onSuccessCallBack }) => {
  const { tenant, user } = useUserContext();
  const { createRequest, updateRequest } = useBlockchainRequest();
  const { tenantid } = useParams();
  const [loading, setLoading] = useState(false);
  const { uploadFile } = useFileUploader();

  const [attributes, setAttributes] = useState<any>({
    logo: '',
    certificates: [],
    invoices: [],
    invoiceNumber: '',
    billDate: new Date().toISOString().split('T')[0],
    productItem: undefined,
    itemDetails: '',
    serialNumber: '',
    certificateType: 'URL'
  });

  const [errors, setErrors] = useState({
    invoiceNumber: false,
    billDate: false,
    productItem: false,
    serialNumber: false
  });

  const [selectedProducts, setSelectedProducts] = useState<Item>(null);
  const [searchParams] = useSearchParams();
  const [catalogue, setCatalogue] = useState([]);
  const { getCatalogueItemsPageWise, getCatalogueTypes } = useCatalogueManagement();
  const [dynamicsFields, setDynamicsFields] = useState<any>();
  const [catalogueTypes, setCatalogueTypes] = useState([]);

  useEffect(() => {
    if (isOpen) {
      handleLoadCatalogue();
      handleLoadTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    console.log("itemImageUrl : ", selectedProducts)
  }, []);

  useEffect(() => {
    if (!requestData) {
      setAttributes((prev) => ({
        ...prev,
        itemDetails: JSON.parse(selectedProducts?.itemDetails ?? "[]"),
      }));
      handleDynamicsFields(selectedProducts?.itemId)
    }
  }, [selectedProducts, catalogue])

  useEffect(() => {
    if (isUpdate) {
      console.log("requestData", requestData)
      setSelectedProducts({
        itemId: requestData?.itemId,
        itemName: requestData?.itemName,
        itemCode: requestData?.itemCode,
        itemDescription: requestData?.itemDescription,
        itemSku: requestData?.itemSku,
        itemCatelogName: requestData?.itemCatelogName,
        itemDetails: requestData?.itemDetails,
        itemImageUrl: requestData?.itemImageUrl,
      });
      onStaticFieldChanges(requestData?.itemId, "productItem");
      handleDynamicsFields(requestData?.itemId);
      setAttributes((prev) => ({
        ...prev,
        invoiceNumber: requestData?.invoiceNumber,
        serialNumber: requestData?.serialNumber,
        billDate: new Date(requestData?.billDate).toISOString().split('T')[0],
        itemDetails: JSON.parse(requestData?.itemDetails ?? "[]"),
        productItem: requestData?.itemId
      }));
    }
  }, [isUpdate, requestData, catalogue])

  const onStaticFieldChanges = (event, type) => {
    const value = event;
    setAttributes((prev) => ({
      ...prev,
      [type]: value
    }))

    // Clear error when user starts typing
    setErrors((prev) => ({ ...prev, [type]: value[0].type ? value[0]?.name?.trim() : value?.trim() === '' }));
  }

  const handleSubmit = async () => {
    setLoading(true);
    const newErrors = {
      invoiceNumber: inputFieldValidator(attributes?.invoiceNumber, "string"),
      billDate: inputFieldValidator(attributes?.billDate, "string"),
      productItem: inputFieldValidator(attributes?.productItem, "string"),
      serialNumber: inputFieldValidator(attributes?.serialNumber, "string")
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((v) => v);
    if (hasError) return setLoading(false);;

    const name = searchParams.get('name');
    const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
    let certificatesfileNames: any = [];
    let certificatesfiles = []

    let invoicesfileNames = [];
    let invoicesfiles = []

    if (attributes?.certificateType === CertificateType.FILE) {
      for (const element of attributes?.certificates) {
        if (element instanceof File) {
          const fileDto = new File(
            [element], // file content
            `${element.name.split('.')[0]}-${tenantid ? tenantid : tenant?.id}-${timestamp}.${element.name.split('.').pop()}`, // new name
            { type: element.type } // original MIME type
          );
          certificatesfiles.push(fileDto);
          certificatesfileNames.push(fileDto?.name);
        }
      }
    }

    if (attributes?.invoices) {
      for (const element of attributes?.invoices) {
        if (element instanceof File) {
          const fileDto = new File(
            [element], // file content
            `${element.name.split('.')[0]}-${tenantid ? tenantid : tenant?.id}-${timestamp}.${element.name.split('.').pop()}`, // new name
            { type: element.type } // original MIME type
          );
          invoicesfiles.push(fileDto);
          invoicesfileNames.push(fileDto?.name);
        }
      }
    }

    const value = attributes?.certificateType === CertificateType.URL ? attributes?.certificates : certificatesfileNames

    let data = {
      requestedToTenantName: tenantid ? name : tenant?.name,
      requestedFromTenantName: tenant?.name,
      requestedToTenantId: tenantid ? tenantid : tenant?.id,
      requestedFromTenantId: tenant?.id,
      requestedBy: user?.email,
      registrationStatusValue: isUpdate ? requestData?.registrationStatusValue : null,
      invoiceNumber: attributes?.invoiceNumber,
      serialNumber: attributes?.serialNumber,
      registrationRequest: null,
      billDate: attributes?.billDate === "" ? new Date() : new Date(`${attributes?.billDate}T00:00:00.000Z`),
      invoiceImageUrls: [
        ...invoicesfileNames
      ],
      certificationUrls: value ? [
        ...value
      ] : [],
      itemImageUrl: selectedProducts?.itemImageUrl || [],
      itemId: selectedProducts?.itemId,
      itemDetails: { MetaDataFields: Object.values(attributes?.itemDetails?.MetaDataFields) },
      itemName: selectedProducts?.itemName,
      ItemCatelogName: selectedProducts?.itemCatelogName,
      itemCode: selectedProducts?.itemCode || tenantid,
      itemDescription: selectedProducts?.itemDescription,
      itemSku: selectedProducts?.itemSku
    };

    if (attributes?.certificateType === CertificateType.FILE) {
      if (attributes?.certificates?.length > 0) {
        let tempImgName = [];
        await Promise.all(
          certificatesfiles?.map((item) =>
            uploadFile("bc-documents", item)
              .then((response) => {
                console.log(response);
                tempImgName.push({
                  Url: response,
                  CertificateType: CertificateType.FILE
                });
              })
              .catch((error) => {
                console.log(error);
              })
          )
        );
        data = {
          ...data,
          certificationUrls: [
            ...tempImgName
          ],
        }
      }
    }

    if (attributes?.invoices?.length > 0) {
      let tempImgName = [];
      await Promise.all(
        invoicesfiles?.map((item) =>
          uploadFile("bc-documents", item)
            .then((response) => {
              console.log(response);
              tempImgName.push(response);
            })
            .catch((error) => {
              console.log(error);
            })
        )
      );
      data = {
        ...data,
        invoiceImageUrls: [
          ...tempImgName
        ]
      }
    }

    try {
      if (isUpdate) await updateRequest({ ...data, id: requestData?.id });
      else await createRequest(data)

      toast.success("Request Process Successfully");
      onSuccessCallBack();
      onClose();
      setAttributes({
        logo: '',
        invoiceNumber: '',
        billDate: new Date().toISOString().split('T')[0],
        productItem: undefined,
        itemDetails: '',
        serialNumber: ''
      });
      setLoading(false);
    } catch (error) {
      console.log("Error : ", error);
      setLoading(false);
      toast.error("Failed to create request");
    }
  }

  const handleLoadCatalogue = () => {
    const data = {
      pageNumber: 1,
      pageSize: 100,
      sortBy: "",
      isAscending: true,
      searchTerm: '',
      tenantId: tenantid ? tenantid : tenant?.id,
      filters: {
        additionalProp1: "string",
        additionalProp2: "string",
        additionalProp3: "string"
      }
    }

    getCatalogueItemsPageWise(data).then((response) => {
      setCatalogue(response?.items);
    }).catch((error) => {
      toast.error("Failed to fetch data");
      console.log(error);
    })
  }

  const onChangeFields = (event, type) => {
    const value = event;
    setAttributes((prev) => ({
      ...prev,
      itemDetails: {
        ...prev?.itemDetails,
        [value?.type]: [
          {
            ...(prev?.itemDetails?.[value?.type]?.[0] || {}),
            [type]: value?.value
          }
        ]
      }
    }));
  }

  const handleDynamicFieldChange = (fieldId: string, value: string | string[] | number | boolean) => {
    console.log("fieldId : ", fieldId, " value : ", value);
    setDynamicsFields((prev) => ({
      ...prev,
      MetaDataFields: prev?.MetaDataFields?.map((field) =>
        field.id === fieldId ? { ...field, fieldValue: value } : field
      ),
    }));
  };

  const handleDynamicsFields = (id) => {
    const filteredCatalogue = catalogue?.find((item) => item?.id === id);
    const filteredItem = catalogueTypes?.find((item) => item?.id === filteredCatalogue?.itemCatalogId);
    const convertStringToJSON = JSON.parse(filteredCatalogue?.itemDetails ?? "[]");
    // console.log("filteredCatalogue : ", filteredCatalogue?.itemDetails);
    // console.log("convertStringToJSON : ", Object.values(convertStringToJSON?.MetaDataFields));

    // convertStringToJSON?.map((item) => {
    //   const childField = item?.childDataField?.length > 0 ? item?.childDataField?.map((subItem) => ({ ...subItem, type: item?.fieldName })) : [{ ...item, type: item?.fieldName }]
    //   fields = [...fields, ...childField]
    // })
    if (convertStringToJSON?.MetaDataFields) {
      setDynamicsFields({ MetaDataFields: Object.values(convertStringToJSON?.MetaDataFields) })
    }
  }

  const handleLoadTypes = () => {
    // Here you would typically send this data to your backend
    getCatalogueTypes(tenantid ? tenantid : tenant?.id).then((response) => {
      setCatalogueTypes(response);
    }).catch((error) => {
      console.log("Error", error);
      toast.error("Failed to fetch Product Types");
    });
  };

  const handleClose = (event) => {
    setAttributes({
      logo: '',
      invoiceNumber: '',
      billDate: new Date().toISOString().split('T')[0],
      productItem: undefined,
      itemDetails: '',
      serialNumber: ''
    });
    setSelectedProducts(null)
    setDynamicsFields([]);
    onClose(event);
  }
  return (
    <div>
      <Sheet open={isOpen} onOpenChange={(event) => handleClose(event)} >
        <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
          <div className="h-full flex flex-col">
            <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-xl font-bold text-gray-900">Add New Order</SheetTitle>
                    <p className="text-sm text-gray-600 mt-1">Add new order to request create blockchain record</p>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={() => handleClose(false)} className="close-panel">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
              </Button>
            </SheetHeader>
            <div className="flex-1 overflow-auto p-6">
              <Tabs defaultValue="details" className="w-full">
                {/* Tabs */}
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="details">Order Details</TabsTrigger>
                  <TabsTrigger value="items">Item Details</TabsTrigger>
                  <TabsTrigger value="certificates">Certificates</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>

                {/* Basic Information Tab */}
                <TabsContent value="details" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Basic Information</CardTitle>
                      <CardDescription>Enter the basic order information</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="serialNumber" className="text-sm font-medium">
                            Serial Number <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="serialNumber"
                            className={errors.serialNumber ? "border-red-500 focus-visible:ring-red-500 mt-1" : "mt-1"}
                            value={attributes?.serialNumber}
                            onChange={(e) => onStaticFieldChanges(e?.target?.value, "serialNumber")}
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <Label
                            htmlFor="invoiceNumber"
                            className="text-sm font-medium"
                          >
                            Invoice Number <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="invoiceNumber"
                            className={errors.invoiceNumber ? "border-red-500 focus-visible:ring-red-500 mt-1" : "mt-1"}
                            value={attributes?.invoiceNumber}
                            onChange={(e) => onStaticFieldChanges(e?.target?.value, "invoiceNumber")}
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="billDate" className="text-sm font-medium">
                            Billing Date <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="billDate"
                            className={errors.billDate ? "border-red-500 focus-visible:ring-red-500 mt-1" : "mt-1"}
                            type="date"
                            value={
                              isUpdate
                                ? (attributes?.billDate && !isNaN(new Date(attributes.billDate).getTime())
                                  ? new Date(attributes.billDate).toISOString().split('T')[0]
                                  : "")
                                : (attributes?.billDate
                                  ? attributes.billDate
                                  : new Date().toISOString().split('T')[0])
                            }

                            onChange={(e) => onStaticFieldChanges(e?.target?.value, "billDate")}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Item Information */}
                <TabsContent value="items" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Item Information</CardTitle>
                      <CardDescription>Enter the item details for this order</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Label htmlFor="productItem" className="text-sm font-medium">
                        Select Product in Catalog <span className="text-red-500">*</span>
                      </Label>

                      <Select
                        value={attributes?.productItem ? attributes?.productItem : undefined}
                        onValueChange={((event: any) => {
                          const selected = catalogue.find((item) => item.id === event);
                          console.log("Selected : ", selected);
                          onStaticFieldChanges(event, "productItem");
                          setSelectedProducts({
                            itemId: selected?.id,
                            itemName: selected?.name,
                            itemCode: selected?.code,
                            itemDescription: selected?.description,
                            itemSku: selected?.sku,
                            itemCatelogName: selected?.itemCatalogName,
                            itemDetails: selected?.itemDetails,
                            itemImageUrl: selected?.imageUrls
                          });
                          // setSelectedProducts(selected);
                        })}
                      >
                        <SelectTrigger
                          className={errors.productItem ? "border-red-500 focus-visible:ring-red-500" : ""}
                        >
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          {catalogue?.map((item) => (
                            <SelectItem key={item?.id} value={item?.id}>
                              {item?.name}
                            </SelectItem>
                          ))}

                        </SelectContent>
                      </Select>

                      {dynamicsFields?.MetaDataFields && dynamicsFields?.MetaDataFields?.length > 0 && (
                        <>
                          <Separator className="bg-border" />
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-foreground">
                              Additional Details
                            </h3>
                            <div className="grid gap-4">
                              {dynamicsFields?.MetaDataFields?.map((field) => (
                                <DynamicFieldRenderer
                                  key={field.id}
                                  field={field}
                                  onChange={handleDynamicFieldChange}
                                />
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {/* {dynamicsFields.length > 0 && (
                        <div className="mt-6">
                          <h3 className="text-lg font-semibold mb-4">Additional Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {dynamicsFields?.map((item, index) => (
                              <div key={index}>
                                {item?.frontEndFieldType === 'checkbox' ? (
                                  <div className="mt-6">
                                    <Checkbox
                                      checked={Array.isArray(attributes?.itemDetails?.[item?.type])
                                        ? attributes?.itemDetails[item?.type][0]?.[item?.fieldName]
                                        : undefined}
                                      id={item?.fieldName}
                                      onCheckedChange={(checked) => onChangeFields({ ...item, value: checked }, item?.fieldName)} />
                                    <Label
                                      id={item?.fieldName}
                                      className="ml-4 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      {item?.displayName}
                                    </Label>
                                  </div>
                                ) : (
                                  <>
                                    <Label className="text-sm font-medium" htmlFor={item?.fieldName}>
                                      {item?.displayName}
                                    </Label>
                                    <Input
                                      id={item?.fieldName}
                                      type={item?.frontEndFieldType}
                                      className="mt-1"
                                      value={Array.isArray(attributes?.itemDetails?.[item?.type])
                                        ? attributes?.itemDetails[item?.type][0]?.[item?.fieldName]
                                        : undefined}
                                      onChange={(e) => onChangeFields({ ...item, value: e.target.value }, item?.fieldName)}
                                    />
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )} */}
                    </CardContent>
                  </Card>
                </TabsContent>


                {/* Upload Documents */}
                <TabsContent value="certificates" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Upload Certificates</CardTitle>
                      <CardDescription>Upload certification documents</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <Select
                        value={attributes?.certificateType}
                        onValueChange={(event) => onStaticFieldChanges(event, "certificateType")}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select the Company Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key={0} value="URL">
                            Upload Url
                          </SelectItem>
                          <SelectItem key={0} value="FILE">
                            Upload File
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {attributes?.certificateType === CertificateType.FILE && (
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                          <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                          <h3 className="font-medium mb-1">Upload Documents</h3>
                          <p className="text-sm text-gray-500 mb-4">Drag and drop or click Files to upload</p>
                          <Input
                            type="file"
                            name="logo"
                            multiple={true}
                            onChange={(event) => {
                              const file = event.target.files;
                              console.log("Files Target : ", event.target.files);
                              console.log("Files : ", file);
                              onStaticFieldChanges(file, "certificates")
                            }}
                          >
                          </Input>
                        </div>
                      )}

                      {attributes?.certificateType === CertificateType.URL && (
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                          <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                          <h3 className="font-medium mb-1">Upload Links</h3>
                          <div className="flex items-center space-x-2">
                            <Input
                              placeholder="Upload Link"
                              name="link"
                              value={attributes?.certificates?.[0]?.Url || ""}
                              onChange={(event) => {
                                const updatedCertificates = [...attributes?.certificates];
                                if (!updatedCertificates[0]) {
                                  updatedCertificates[0] = { Url: event?.target?.value, CertificateUrlType: attributes?.certificateType };
                                } else {
                                  updatedCertificates[0].Url = event?.target?.value;
                                }
                                setAttributes((prev) => ({
                                  ...prev,
                                  certificates: updatedCertificates
                                }));
                              }}
                            />
                            <Button
                              className="bg-green-500 hover:bg-green-600 text-white p-2 rounded"
                              onClick={() => {
                                setAttributes((prev) => ({
                                  ...prev,
                                  certificates: [...prev?.certificates, { Url: "", CertificateUrlType: attributes?.certificateType }]
                                }));
                              }}
                            >
                              <span className="material-icons"><Plus /></span>
                            </Button>
                          </div>
                          {attributes?.certificates?.slice(1).map((cert, index) => (
                            <div key={index + 1} className="flex items-center space-x-2 mt-2">
                              <Input
                                placeholder="Upload Link"
                                name={`link-${index + 1}`}
                                value={cert.Url}
                                onChange={(event) => {
                                  const updatedCertificates = [...attributes?.certificates];
                                  updatedCertificates[index + 1] = {
                                    ...updatedCertificates[index + 1],
                                    Url: event?.target?.value
                                  };
                                  setAttributes((prev) => ({
                                    ...prev,
                                    certificates: updatedCertificates
                                  }));
                                }}
                              />
                              <Button
                                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded"
                                onClick={() => {
                                  const updatedCertificates = attributes?.certificates?.filter((_, i) => i !== index + 1);
                                  setAttributes((prev) => ({
                                    ...prev,
                                    certificates: updatedCertificates
                                  }));
                                }}
                              >
                                <span className="material-icons"><DeleteIcon /></span>
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                    </CardContent>
                  </Card>
                </TabsContent>


                {/* Upload Documents */}
                <TabsContent value="documents" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Upload Documents</CardTitle>
                      <CardDescription>Upload documents documents</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                        <h3 className="font-medium mb-1">Upload Documents</h3>
                        <p className="text-sm text-gray-500 mb-4">Drag and drop or click Files to upload</p>
                        <Input
                          type="file"
                          name="logo"
                          multiple={true}
                          onChange={(event) => {
                            const file = event.target.files;
                            console.log("Files Target : ", event.target.files);
                            console.log("Files : ", file);
                            onStaticFieldChanges(file, "invoices")
                          }}
                        >
                        </Input>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            <div className="border-t p-6 flex justify-end gap-2 bg-gray-50 detail-header">
              <Button className="bg-purple-600 hover:bg-purple-700 bg-primary" onClick={() => handleSubmit()} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Order"
                )}
              </Button>
              {/* <Button variant="outline" onClick={() => handleClose(false)} className="bg-secondary">
                Cancel
              </Button> */}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default AddRequest;
